import React, { useEffect, useState, useContext } from "react";
import { Button, Table, Modal, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { AuthContext } from "../../hooks/auth/AuthContext";
import Head from "../../components/layout/head/Head";
import { toast } from "react-toastify";
import api from "../../services/api";

const EstablecimientosPage = () => {
    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [establecimientos, setEstablecimientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [incluirInactivos, setIncluirInactivos] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        id: "",
        dEst: "",
        descripcion: "",
        dDirEmi: "",
        dNumCas: 0,
        dCompDir1: "",
        dCompDir2: "",
        dTelEmi: "",
        dEmailE: "",
        dDenSuc: "",
        activo: true,
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [incluirInactivos]);

    const loadData = async () => {
        try {
            setLoading(true);
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` },
                params: { soloActivos: !incluirInactivos }
            };
            const response = await api.get("/api/establecimientos", config);
            setEstablecimientos(response.data);
        } catch (error) {
            console.error("Error al cargar establecimientos:", error);
            toast.error("Error al cargar los establecimientos");
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = async (item = null) => {
        if (item) {
            setEditMode(true);
            setCurrentItem(item);
            setFormData({
                id: item.id,
                dEst: item.dEst || "",
                descripcion: item.descripcion || "",
                dDirEmi: item.dDirEmi || "",
                dNumCas: item.dNumCas || 0,
                dCompDir1: item.dCompDir1 || "",
                dCompDir2: item.dCompDir2 || "",
                dTelEmi: item.dTelEmi || "",
                dEmailE: item.dEmailE || "",
                dDenSuc: item.dDenSuc || "",
                activo: item.activo !== undefined ? item.activo : true,
            });
        } else {
            setEditMode(false);
            setCurrentItem(null);
            setFormData({
                id: "",
                dEst: "",
                descripcion: "",
                dDirEmi: "",
                dNumCas: 0,
                dCompDir1: "",
                dCompDir2: "",
                dTelEmi: "",
                dEmailE: "",
                dDenSuc: "",
                activo: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.dEst || formData.dEst.trim() === "") {
            errors.dEst = "El código de establecimiento es obligatorio";
        } else if (formData.dEst.length !== 3) {
            errors.dEst = "El código debe tener exactamente 3 dígitos";
        }

        if (!formData.descripcion || formData.descripcion.trim() === "") {
            errors.descripcion = "La descripción es obligatoria";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const config = {
                headers: { 'Authorization': `Bearer ${user.token}` }
            };

            const dataToSend = {
                DEst: formData.dEst,
                Descripcion: formData.descripcion,
                DDirEmi: formData.dDirEmi,
                DNumCas: parseInt(formData.dNumCas) || 0,
                DCompDir1: formData.dCompDir1,
                DCompDir2: formData.dCompDir2,
                DTelEmi: formData.dTelEmi,
                DEmailE: formData.dEmailE,
                DDenSuc: formData.dDenSuc,
                Activo: formData.activo,
            };

            if (editMode) {
                dataToSend.Id = formData.id;
                await api.put(`/api/establecimientos/${formData.id}`, dataToSend, config);
                toast.success("Establecimiento actualizado correctamente");
            } else {
                await api.post("/api/establecimientos", dataToSend, config);
                toast.success("Establecimiento creado correctamente");
            }

            handleCloseModal();
            loadData();
        } catch (error) {
            console.error("Error al guardar establecimiento:", error);
            if (error.response?.data) {
                toast.error(typeof error.response.data === 'string' ? error.response.data : "Error al guardar el establecimiento");
            } else {
                toast.error("Error al guardar el establecimiento");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <>
            <Head title="Configuración - Establecimientos" />
            <div className="container-fluid mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2>Gestión de Establecimientos</h2>
                    <div className="d-flex gap-3 align-items-center">
                        <Form.Check
                            type="checkbox"
                            label="Incluir inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                        />
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            Nuevo Establecimiento
                        </Button>
                    </div>
                </div>

                {establecimientos.length === 0 ? (
                    <Alert variant="info">No hay establecimientos registrados.</Alert>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Descripción</th>
                                    <th>Dirección</th>
                                    <th>Teléfono</th>
                                    <th>Email</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {establecimientos.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.dEst}</td>
                                        <td>{item.descripcion}</td>
                                        <td>{item.dDirEmi}</td>
                                        <td>{item.dTelEmi}</td>
                                        <td>{item.dEmailE}</td>
                                        <td>
                                            <Badge bg={item.activo ? "success" : "secondary"}>
                                                {item.activo ? "Activo" : "Inactivo"}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleShowModal(item)}
                                            >
                                                Editar
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal para Crear/Editar */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editMode ? "Editar Establecimiento" : "Nuevo Establecimiento"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Código Establecimiento (3 dígitos) *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dEst"
                                    value={formData.dEst}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.dEst}
                                    maxLength={3}
                                    placeholder="Ej: 001"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.dEst}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Descripción *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.descripcion}
                                    placeholder="Ej: Casa Matriz"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.descripcion}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Denominación Sucursal</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dDenSuc"
                                    value={formData.dDenSuc}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Dirección</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dDirEmi"
                                    value={formData.dDirEmi}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Número de Casa</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="dNumCas"
                                    value={formData.dNumCas}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Complemento Dirección 1</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dCompDir1"
                                    value={formData.dCompDir1}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Complemento Dirección 2</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dCompDir2"
                                    value={formData.dCompDir2}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Teléfono</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="dTelEmi"
                                    value={formData.dTelEmi}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="dEmailE"
                                    value={formData.dEmailE}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>

                            {editMode && (
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="activo"
                                        label="Activo"
                                        checked={formData.activo}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Guardando...
                                    </>
                                ) : editMode ? (
                                    "Actualizar"
                                ) : (
                                    "Crear"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default EstablecimientosPage;
